import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Select, Switch } from "antd";
import { Loader2, MessageCircle, Save } from "lucide-react";
import { ContainerCard } from "../ui/cards";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../common/ToastMsg";
import { EMAIL_LEAD_WELCOME_URL, EMAIL_LEAD_WELCOME_LOG_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";

const STATUS_STYLE = {
    pending: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    sending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    sent:    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    skipped: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
    failed:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};
const STATUS_LABEL = { pending: 'Waiting', sending: 'Sending', sent: 'Sent', skipped: 'Not sent', failed: 'Failed' };
export const TEXT = '__text';   // "fixed text" in the field picker: the spec is the text itself

const fmt = (d) => (d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '');

/** The {{n}} positions a template takes, in its body and its buttons, in order. */
export function templatePositions(template) {
    if (!template) return [];
    const text = `${template.body_text || ''} ${template.buttons || ''}`;
    const found = new Set([...text.matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map(m => m[1]));
    return [...found].sort((a, b) => Number(a) - Number(b));
}

/** 'contact_name|there' -> { field: 'contact_name', fallback: 'there' };
 *  anything that is not a known field is fixed text. */
export function splitSpec(spec, fieldKeys) {
    const [head, ...rest] = String(spec || '').split('|');
    const field = head.trim();
    if (fieldKeys.includes(field)) return { field, fallback: rest.join('|').trim(), text: '' };
    return { field: field ? TEXT : '', fallback: '', text: String(spec || '') };
}

export const joinSpec = ({ field, fallback, text }) =>
    field === TEXT ? text : (field ? (fallback ? `${field}|${fallback}` : field) : '');

/**
 * Settings → Email Templates: the approved WhatsApp template every new lead
 * gets, about two minutes after it arrives. Backend: the wa_* fields of
 * email/lead-welcome/ (platform_core/email_integration/lead_welcome_whatsapp.py).
 * Off until switched on.
 */
export default function LeadWelcomeWhatsAppCard() {
    const { fetch, update } = useApiMethods();
    const navigate = useNavigate();
    const [form, setForm] = useState(null);
    const [options, setOptions] = useState({ wa_accounts: [], wa_templates: [], wa_fields: [] });
    const [hasPageUrl, setHasPageUrl] = useState(false);
    const [log, setLog] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const take = (data) => {
        setForm({ wa_enabled: data.wa_enabled, wa_account_id: data.wa_account_id,
                  wa_template_id: data.wa_template_id, wa_variables: data.wa_variables || {} });
        setOptions({ wa_accounts: data.wa_accounts || [], wa_templates: data.wa_templates || [],
                     wa_fields: data.wa_fields || [] });
        setHasPageUrl(!!data.profile_page_url);
    };

    const load = async () => {
        const [cfg, recent] = await Promise.all([
            fetch(EMAIL_LEAD_WELCOME_URL), fetch(`${EMAIL_LEAD_WELCOME_LOG_URL}?channel=whatsapp`),
        ]);
        if (cfg?.status === 'success') {
            take(cfg.data);
            setError('');
        } else {
            setError(cfg?.message || 'Could not load the WhatsApp welcome settings');
        }
        if (recent?.status === 'success') setLog(recent.data || []);
    };

    useEffect(() => { load(); }, []);

    const fieldKeys = options.wa_fields.map(f => f.key);
    const template = options.wa_templates.find(t => t.id === form?.wa_template_id);
    const positions = useMemo(() => templatePositions(template), [template]);

    const save = async (patch = {}) => {
        // Only the variables this template takes, so a switch of template
        // leaves no stale ones behind.
        const vars = Object.fromEntries(positions.map(p => [p, form.wa_variables[p] || '']));
        setSaving(true);
        const res = await update(EMAIL_LEAD_WELCOME_URL, { ...form, wa_variables: vars, ...patch });
        setSaving(false);
        if (res?.status === 'success') {
            take(res.data);
            ToastMsg('success', patch.wa_enabled === undefined ? 'Saved'
                : patch.wa_enabled ? 'WhatsApp welcome switched on' : 'WhatsApp welcome switched off');
        } else {
            ToastMsg('error', res?.message || 'Save failed');
        }
    };

    if (error) {
        return <ContainerCard><p className="text-sm text-red-600 dark:text-red-400">{error}</p></ContainerCard>;
    }
    if (!form) {
        return (
            <ContainerCard>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading WhatsApp welcome…
                </div>
            </ContainerCard>
        );
    }

    const setVar = (pos, part) => setForm(p => {
        const cur = splitSpec(p.wa_variables[pos], fieldKeys);
        return { ...p, wa_variables: { ...p.wa_variables, [pos]: joinSpec({ ...cur, ...part }) } };
    });
    const accountTemplates = options.wa_templates.filter(
        t => !form.wa_account_id || !t.account_id || t.account_id === form.wa_account_id);
    const usesLink = positions.some(p => ['verification_link', 'confirm_code']
        .includes(splitSpec(form.wa_variables[p], fieldKeys).field));
    const fieldOptions = [
        ...options.wa_fields.map(f => ({ value: f.key, label: f.label })),
        { value: TEXT, label: 'Fixed text…' },
    ];

    return (
        <ContainerCard>
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-green-50 dark:bg-green-900/30 flex items-center justify-center shrink-0">
                        <MessageCircle className="h-4 w-4 text-green-600 dark:text-green-300" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">New lead welcome WhatsApp</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            Every new lead with a mobile number gets the chosen approved template once, about two minutes
                            after it arrives. Imported sheets, existing customers, opted-out numbers and leads your team has
                            already WhatsApped are left out. A lead who opens the confirm-details link has their mobile verified.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 dark:text-gray-300">{form.wa_enabled ? 'On' : 'Off'}</span>
                    <Switch checked={form.wa_enabled} loading={saving} aria-label="WhatsApp welcome"
                            onChange={(v) => save({ wa_enabled: v })} />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Send from</label>
                    <Select className="w-full" placeholder="Choose a WhatsApp account" allowClear
                            value={form.wa_account_id ?? undefined}
                            options={options.wa_accounts.map(a => ({
                                value: a.id, label: `${a.name}${a.phone_number ? ` (${a.phone_number})` : ''}`,
                            }))}
                            notFoundContent={<span className="text-xs text-gray-500">No WhatsApp account connected</span>}
                            onChange={(v) => setForm(p => ({ ...p, wa_account_id: v ?? null }))} />
                </div>
                <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Template</label>
                    <Select className="w-full" placeholder="Choose an approved template" allowClear showSearch
                            optionFilterProp="label" value={form.wa_template_id ?? undefined}
                            options={accountTemplates.map(t => ({ value: t.id, label: `${t.name} · ${t.category}` }))}
                            notFoundContent={<span className="text-xs text-gray-500">No approved templates - sync them under Settings → WA Templates</span>}
                            onChange={(v) => setForm(p => ({ ...p, wa_template_id: v ?? null }))} />
                    {template?.category === 'marketing' && (
                        <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
                            Marketing template: Meta may hold it back from numbers that recently got other marketing messages.
                        </p>
                    )}
                </div>
            </div>

            {template && (
                <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="rounded-lg bg-green-50/60 dark:bg-green-900/10 border border-green-100 dark:border-green-900/40 p-3">
                        <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Message{template.header_type && template.header_type !== 'none' ? ` (with ${template.header_type} header)` : ''}
                        </p>
                        <p className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap">{template.body_text}</p>
                    </div>
                    <div className="space-y-3">
                        {positions.length === 0 && (
                            <p className="text-xs text-gray-500">This template takes no variables.</p>
                        )}
                        {positions.map(pos => {
                            const v = splitSpec(form.wa_variables[pos], fieldKeys);
                            return (
                                <div key={pos}>
                                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
                                        {`{{${pos}}}`}
                                    </label>
                                    <div className="flex gap-2">
                                        <Select className="flex-1 min-w-0" placeholder="Fill with…" value={v.field || undefined}
                                                options={fieldOptions} aria-label={`Variable ${pos}`}
                                                onChange={(f) => setVar(pos, { field: f, text: f === TEXT ? v.text : '' })} />
                                        {v.field === TEXT ? (
                                            <Input className="flex-1" value={v.text} placeholder="Text to send"
                                                   onChange={(e) => setVar(pos, { text: e.target.value })} />
                                        ) : !['verification_link', 'confirm_code', ''].includes(v.field) && (
                                            <Input className="flex-1" value={v.fallback} placeholder="If blank, send…"
                                                   onChange={(e) => setVar(pos, { fallback: e.target.value })} />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                            For a button whose URL ends in a variable (…/confirm/{'{{2}}'}) choose
                            <b> Confirm-details code (button)</b>. Without a fallback, a lead missing that field is skipped
                            and the reason shows below.
                        </p>
                        {usesLink && !hasPageUrl && (
                            <p className="text-[11px] text-red-600 dark:text-red-400">
                                Set the Details page URL in the welcome email card above first.
                            </p>
                        )}
                    </div>
                </div>
            )}

            <div className="flex justify-end mt-4">
                <Button onClick={() => save()} disabled={saving} className="text-sm flex items-center gap-2">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                </Button>
            </div>

            <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Recent leads</h3>
                {log.length === 0 ? (
                    <p className="text-xs text-gray-400">No new leads since the WhatsApp welcome was switched on.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                                    <th className="pb-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400">Lead</th>
                                    <th className="pb-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400">Status</th>
                                    <th className="pb-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400">To</th>
                                    <th className="pb-2 text-xs font-semibold text-gray-500 dark:text-gray-400">When</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                {log.map(r => (
                                    <tr key={r.id}>
                                        <td className="py-2 pr-4">
                                            <button type="button" className="text-brand-600 dark:text-brand-300 hover:underline text-left"
                                                    onClick={() => navigate(`/customers/customerdetails?id=${r.customer_id}`,
                                                        { state: { formEdit: true, row: { id: r.customer_id } } })}>
                                                {r.customer_name || `Lead #${r.customer_id}`}
                                            </button>
                                        </td>
                                        <td className="py-2 pr-4">
                                            <span className={cn("px-2 py-0.5 text-[10px] font-medium rounded-full", STATUS_STYLE[r.status])}>
                                                {STATUS_LABEL[r.status] || r.status}
                                            </span>
                                            {r.reason && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{r.reason}</span>}
                                        </td>
                                        <td className="py-2 pr-4 text-xs text-gray-600 dark:text-gray-300">{r.to_phone || '—'}</td>
                                        <td className="py-2 text-xs text-gray-500 dark:text-gray-400">{fmt(r.sent_at || r.created_date)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </ContainerCard>
    );
}
